/**
 * ExportModal.tsx
 * Modal complet pour l'export de projets
 */
import React, { useState, useCallback, useEffect } from 'react';
import { Modal } from './common/Modal';
import { useAppStore } from '../../services/StateService';
// PR-05: Plus besoin de useAppCoordinator, on utilise stageRef directement
import { ExportService, type ExportOptions, type ExportResult } from '../../services/ExportService';
import { useToast } from './common/ToastProvider';
import { logger } from '../../utils/ProfessionalLogger';
import { ExportPreviewModal } from './ExportPreviewModal';
import { useCanvasStageRef } from '../../hooks/useCanvasStageRef';
import { studioStatsService } from '../../services/StudioStatsService';
import { exportCanvasBundle } from '../../canvas/runtime/CanvasRuntime';
// P3 : exportPNG retiré du runtime ; on garde un stub local pour compat.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _exportPNG = ((..._args: unknown[]) => '') as any;
import { Button, Select, Card, Divider } from '../../ui';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ExportModal: React.FC<ExportModalProps> = ({ isOpen, onClose }) => {
  // PR-05: Plus besoin d'appCoordinator, on utilise stageRef directement
  const [format, setFormat] = useState<'png' | 'jpg' | 'webp' | 'pdf' | 'svg'>('png');
  const [dpi, setDpi] = useState<number>(300);
  const [quality, setQuality] = useState<number>(1);
  const [pixelRatio, setPixelRatio] = useState<number>(3);
  const [isExporting, setIsExporting] = useState(false);
  const [previewSize, setPreviewSize] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  const { addToast } = useToast();

  const templateWidth = useAppStore(state => state.canvas.templateWidth);
  const templateHeight = useAppStore(state => state.canvas.templateHeight);
  const backgroundColor = useAppStore(state => state.canvas.backgroundColor);
  const shapes = useAppStore(state => state.shapes);
  const projectId = useAppStore(state => state.project.id);
  const stageRef = useCanvasStageRef();

  // Calculer la taille estimée du fichier
  useEffect(() => {
    if (!isOpen) return;

    const widthPx = Math.round((templateWidth * dpi) / 96);
    const heightPx = Math.round((templateHeight * dpi) / 96);
    const pixels = widthPx * heightPx;

    let estimatedSize = 0;
    switch (format) {
      case 'png':
        estimatedSize = pixels * 4; // RGBA
        break;
      case 'jpg':
        estimatedSize = pixels * 3 * quality; // RGB avec compression
        break;
      case 'webp':
        estimatedSize = pixels * 3 * quality * 0.8; // WebP plus efficace
        break;
      case 'pdf':
        estimatedSize = pixels * 0.5; // PDF vectoriel
        break;
      case 'svg':
        estimatedSize = 50000; // SVG généralement petit
        break;
    }

    const sizeMB = (estimatedSize / (1024 * 1024)).toFixed(2);
    setPreviewSize(`${widthPx}×${heightPx}px (~${sizeMB} MB)`);
  }, [format, dpi, quality, templateWidth, templateHeight, isOpen]);

  const handleExport = useCallback(async () => {
    setIsExporting(true);

    try {
      // PR-05: Utiliser stageRef directement au lieu de appCoordinator.getCanvasManager()
      const stage = stageRef?.current;
      if (!stage) {
        throw new Error('Stage non disponible');
      }

      const exportService = new ExportService(stage);

      const options: ExportOptions = {
        format,
        dpi,
        quality: format === 'jpg' || format === 'webp' ? quality : undefined,
        pixelRatio,
        backgroundColor,
        filename: `snapbooth-export-${Date.now()}.${format}`,
      };

      let result: ExportResult;

      switch (format) {
        case 'png':
          result = await exportService.exportToPNG(options);
          break;
        case 'jpg':
          result = await exportService.exportToJPG(options);
          break;
        case 'webp':
          result = await exportService.exportToWebP(options);
          break;
        case 'pdf':
          result = await exportService.exportToPDF(options);
          break;
        case 'svg':
          result = await exportService.exportToSVG(options);
          break;
        default:
          throw new Error(`Format non supporté: ${format}`);
      }

      if (!result.success || !result.data) {
        throw new Error(result.error || "Erreur lors de l'export");
      }

      // Télécharger le fichier
      const blob =
        result.data instanceof Blob
          ? result.data
          : await (async () => {
              if (typeof result.data === 'string') {
                // DataURL
                const response = await fetch(result.data);
                return await response.blob();
              }
              throw new Error('Format de données non supporté');
            })();

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename || `export.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      addToast({
        type: 'success',
        message: `Export réussi: ${result.filename}`,
        duration: 3000,
      });

      // Enregistre l'export réel dans Supabase (alimente Live + Kiosque).
      // No-op si le projet n'est pas encore sauvegardé.
      void studioStatsService.recordExport({
        projectId,
        format,
        filename: result.filename || `export.${format}`,
        sizeBytes: result.size ?? blob.size ?? null,
        dpi,
        width: Math.round((templateWidth * dpi) / 96),
        height: Math.round((templateHeight * dpi) / 96),
        pixelRatio,
      });

      logger.info(`✅ Export réussi: ${format} - ${result.size} bytes`);
      onClose();
    } catch (error) {
      logger.error("Erreur lors de l'export:", error);
      addToast({
        type: 'error',
        message: error instanceof Error ? error.message : "Erreur lors de l'export",
        duration: 5000,
      });
    } finally {
      setIsExporting(false);
    }
  }, [
    stageRef,
    format,
    dpi,
    quality,
    pixelRatio,
    backgroundColor,
    templateWidth,
    templateHeight,
    projectId,
    addToast,
    onClose,
  ]);

  // Handler pour exporter le bundle (PNG + JSON)
  const handleExportBundle = useCallback(async () => {
    setIsExporting(true);

    try {
      if (!stageRef) {
        throw new Error('Stage non disponible');
      }

      const bundle = exportCanvasBundle(stageRef, shapes, pixelRatio);
      if (!bundle) {
        throw new Error("Échec de l'export du bundle");
      }

      // Télécharger le PNG
      const pngBlob = await fetch(bundle.pngDataUrl).then(res => res.blob());
      const pngUrl = URL.createObjectURL(pngBlob);
      const pngLink = document.createElement('a');
      pngLink.href = pngUrl;
      pngLink.download = `snapbooth-export-${Date.now()}.png`;
      document.body.appendChild(pngLink);
      pngLink.click();
      document.body.removeChild(pngLink);
      URL.revokeObjectURL(pngUrl);

      // Télécharger le JSON
      const jsonBlob = new Blob([bundle.json], { type: 'application/json' });
      const jsonUrl = URL.createObjectURL(jsonBlob);
      const jsonLink = document.createElement('a');
      jsonLink.href = jsonUrl;
      jsonLink.download = `snapbooth-export-${Date.now()}.json`;
      document.body.appendChild(jsonLink);
      jsonLink.click();
      document.body.removeChild(jsonLink);
      URL.revokeObjectURL(jsonUrl);

      addToast({
        type: 'success',
        message: `Bundle exporté: PNG + JSON (${bundle.meta.shapeCount} shape(s))`,
        duration: 3000,
      });

      // A09 : enregistrer l'export bundle dans les stats (comme l'export simple),
      // afin que les compteurs Live/Kiosque restent cohérents.
      void studioStatsService.recordExport({
        projectId,
        format: 'bundle',
        filename: `snapbooth-export-${Date.now()}.png+json`,
        sizeBytes: pngBlob.size + jsonBlob.size,
        dpi: null,
        width: bundle.meta.width,
        height: bundle.meta.height,
        pixelRatio,
      });

      logger.info(
        `✅ Bundle exporté: ${bundle.meta.shapeCount} shape(s), ${bundle.meta.width}x${bundle.meta.height}px`
      );
      onClose();
    } catch (error) {
      logger.error("Erreur lors de l'export du bundle:", error);
      addToast({
        type: 'error',
        message: error instanceof Error ? error.message : "Erreur lors de l'export du bundle",
        duration: 5000,
      });
    } finally {
      setIsExporting(false);
    }
  }, [stageRef, shapes, pixelRatio, projectId, addToast, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Exporter le projet" size="medium">
      <div className="flex flex-col gap-6">
        {/* Sélection du format */}
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">Format d'export</label>
          <div className="grid grid-cols-5 gap-2">
            {(['png', 'jpg', 'webp', 'pdf', 'svg'] as const).map(fmt => (
              <Button
                key={fmt}
                variant={format === fmt ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setFormat(fmt)}
                className="uppercase text-xs"
              >
                {fmt}
              </Button>
            ))}
          </div>
        </div>

        <Divider />

        {/* Options de qualité */}
        <div>
          <Select
            label="DPI (Résolution)"
            value={dpi.toString()}
            onChange={e => {
              const newDpi = parseInt(e.target.value, 10);
              setDpi(newDpi);
              // Calculer pixelRatio automatiquement
              setPixelRatio(Math.round((newDpi / 96) * 100) / 100);
            }}
            options={[
              { value: '72', label: '72 DPI (Web)' },
              { value: '150', label: '150 DPI (Standard)' },
              { value: '300', label: '300 DPI (Impression haute qualité)' },
              { value: '600', label: '600 DPI (Professionnel)' },
            ]}
            fullWidth
          />
          <div className="text-xs text-gray-500 mt-1">Pixel Ratio: {pixelRatio.toFixed(2)}x</div>
        </div>

        {/* Qualité pour JPG/WebP */}
        {(format === 'jpg' || format === 'webp') && (
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Qualité: {Math.round(quality * 100)}%
            </label>
            <input
              type="range"
              min="0.1"
              max="1"
              step="0.1"
              value={quality}
              onChange={e => setQuality(parseFloat(e.target.value))}
              className="w-full"
            />
            <div className="text-xs text-gray-500 mt-1">
              Plus la qualité est élevée, plus le fichier est volumineux
            </div>
          </div>
        )}

        <Divider />

        {/* Prévisualisation */}
        <Card padding="md" shadow="sm">
          <div className="font-semibold mb-1 text-sm">Prévisualisation</div>
          <div className="text-sm text-gray-600">Dimensions: {previewSize || 'Calcul...'}</div>
          <div className="text-sm text-gray-600 mt-1">Format: {format.toUpperCase()}</div>
        </Card>

        {/* Boutons d'action */}
        <div className="flex gap-3 justify-end flex-wrap">
          <Button variant="secondary" size="md" onClick={onClose} disabled={isExporting}>
            Annuler
          </Button>
          <Button
            variant="secondary"
            size="md"
            onClick={() => setShowPreview(true)}
            disabled={isExporting}
          >
            Prévisualiser
          </Button>
          <Button
            variant="success"
            size="md"
            onClick={handleExportBundle}
            disabled={isExporting || !stageRef}
            isLoading={isExporting}
            title="Exporter PNG + JSON avec métadonnées"
          >
            {isExporting ? 'Export en cours...' : 'Exporter Bundle (PNG + JSON)'}
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleExport}
            disabled={isExporting}
            isLoading={isExporting}
          >
            {isExporting ? 'Export en cours...' : 'Exporter'}
          </Button>
        </div>
      </div>

      {/* ExportPreviewModal */}
      {showPreview && (
        <ExportPreviewModal
          isOpen={showPreview}
          onClose={() => setShowPreview(false)}
          initialFormat={format}
          initialDpi={dpi}
          initialQuality={quality}
          onExport={async (exportFormat, options) => {
            // Utiliser les options passées ou les valeurs par défaut
            const exportDpi = options?.dpi || dpi;
            const exportQuality = options?.quality || quality;

            // Mettre à jour les valeurs locales si différentes
            if (exportDpi !== dpi) setDpi(exportDpi);
            if (exportQuality !== quality) setQuality(exportQuality);
            if (exportFormat !== format) setFormat(exportFormat as typeof format);

            // Fermer le preview et exporter
            setShowPreview(false);

            // Utiliser handleExport avec les nouvelles valeurs
            setIsExporting(true);
            try {
              // PR-05: Utiliser stageRef directement au lieu de appCoordinator.getCanvasManager()
              const stage = stageRef?.current;
              if (!stage) {
                throw new Error('Stage non disponible');
              }

              const exportService = new ExportService(stage);
              const exportOptions: ExportOptions = {
                format: exportFormat as typeof format,
                dpi: exportDpi,
                quality:
                  exportFormat === 'jpg' || exportFormat === 'webp' ? exportQuality : undefined,
                pixelRatio: Math.round((exportDpi / 96) * 100) / 100,
                backgroundColor,
                filename: `snapbooth-export-${Date.now()}.${exportFormat}`,
              };

              let result: ExportResult;
              switch (exportFormat) {
                case 'png':
                  result = await exportService.exportToPNG(exportOptions);
                  break;
                case 'jpg':
                  result = await exportService.exportToJPG(exportOptions);
                  break;
                case 'webp':
                  result = await exportService.exportToWebP(exportOptions);
                  break;
                case 'pdf':
                  result = await exportService.exportToPDF(exportOptions);
                  break;
                case 'svg':
                  result = await exportService.exportToSVG(exportOptions);
                  break;
                default:
                  throw new Error(`Format non supporté: ${exportFormat}`);
              }

              if (!result.success || !result.data) {
                throw new Error(result.error || "Erreur lors de l'export");
              }

              const blob =
                result.data instanceof Blob
                  ? result.data
                  : await (async () => {
                      if (typeof result.data === 'string') {
                        const response = await fetch(result.data);
                        return await response.blob();
                      }
                      throw new Error('Format de données non supporté');
                    })();

              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = result.filename || `export.${exportFormat}`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);

              addToast({
                type: 'success',
                message: `Export réussi: ${result.filename}`,
                duration: 3000,
              });

              // A08 : enregistrer aussi l'export lancé depuis l'aperçu
              // (auparavant seul l'export direct alimentait les stats).
              void studioStatsService.recordExport({
                projectId,
                format: exportFormat,
                filename: result.filename || `export.${exportFormat}`,
                sizeBytes: result.size ?? blob.size ?? null,
                dpi: exportDpi,
                width: Math.round((templateWidth * exportDpi) / 96),
                height: Math.round((templateHeight * exportDpi) / 96),
                pixelRatio: exportOptions.pixelRatio,
              });

              logger.info(`✅ Export réussi: ${exportFormat} - ${result.size} bytes`);
              onClose();
            } catch (error) {
              logger.error("Erreur lors de l'export:", error);
              addToast({
                type: 'error',
                message: error instanceof Error ? error.message : "Erreur lors de l'export",
                duration: 5000,
              });
            } finally {
              setIsExporting(false);
            }
          }}
        />
      )}
    </Modal>
  );
};
